import FormatQuoteRoundedIcon from '@mui/icons-material/FormatQuoteRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { Box, Theme, Typography } from '@mui/material';

import { useLanguage, useSearchMode } from '../../hooks';

interface SearchModeSuggestionsProps {
  /** Current query text from the search input. */
  query: string;
  /** Whether the suggestions dropdown is open (input focused + non-empty query). */
  open: boolean;
  /** Called after a mode is picked, so the parent can close the dropdown. */
  onClose: () => void;
}

const customStyles = {
  card: {
    border: '1px solid #e8e8e8',
    borderRadius: '8px',
    backgroundColor: '#FFFFFF',
    padding: '8px 0',
    marginBottom: '2vh',
  },
  title: {
    fontSize: (theme: Theme) => theme.typography.caption.fontSize,
    color: '#525459', // Neutral-8
    padding: '0 16px 4px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '8px 16px',
    cursor: 'pointer',
    '&:hover': { backgroundColor: '#F3F5F9' },
  },
  rowActive: {
    backgroundColor: '#D8EFFE', // Primary-1
    '&:hover': { backgroundColor: '#D8EFFE' },
  },
  rowIcon: {
    fontSize: '20px',
    color: '#525459', // Neutral-8
  },
  rowIconActive: {
    color: '#3E8FFA', // Primary-5
  },
  rowText: { minWidth: 0 },
  rowLabel: {
    fontSize: (theme: Theme) => theme.typography.body2.fontSize,
    fontWeight: 500,
    color: '#3B3C40', // Neutral-10
  },
  rowHint: {
    fontSize: (theme: Theme) => theme.typography.caption.fontSize,
    color: '#525459', // Neutral-8
  },
  pill: {
    display: 'inline-flex',
    alignSelf: 'flex-start', // compact tag, not full-width (matches the app)
    alignItems: 'center',
    gap: '6px',
    backgroundColor: '#D8EFFE', // Primary-1
    borderRadius: '12px',
    padding: '4px 10px',
    marginBottom: '2vh',
  },
  pillIcon: { fontSize: '16px', color: '#3E8FFA' },
  pillText: {
    fontSize: (theme: Theme) => theme.typography.caption.fontSize,
    color: '#3E8FFA', // Primary-5
  },
};

/**
 * Search-mode picker styled after the mobile app: a "Sugerencias de búsqueda" dropdown
 * (general vs. exact) shown while the user is searching, plus a compact active-mode pill
 * once Exact is active. Owns nothing but reads/writes the shared mode via useSearchMode.
 */
export const SearchModeSuggestions = ({ query, open, onClose }: SearchModeSuggestionsProps) => {
  const lang = useLanguage();
  const { searchMode, setExactMatch } = useSearchMode();
  const trimmedQuery = query.trim();

  const pick = (exact: boolean) => () => {
    setExactMatch(exact);
    onClose();
  };

  if (open && trimmedQuery.length > 0) {
    const renderRow = (
      active: boolean,
      icon: JSX.Element,
      label: string,
      hint: string,
      onPick: () => void,
    ) => (
      <Box
        // Keep focus on the input so onBlur doesn't close the dropdown before the click.
        onMouseDown={(event) => event.preventDefault()}
        onClick={onPick}
        sx={[customStyles.row, active && customStyles.rowActive]}
      >
        {icon}
        <Box sx={customStyles.rowText}>
          <Typography sx={customStyles.rowLabel} noWrap>
            {label}
          </Typography>
          <Typography sx={customStyles.rowHint} noWrap>
            {hint}
          </Typography>
        </Box>
      </Box>
    );

    return (
      <Box sx={customStyles.card}>
        <Typography sx={customStyles.title}>
          {lang.form_answer.search_suggestions_title}
        </Typography>
        {renderRow(
          searchMode === 'general',
          <SearchRoundedIcon
            sx={[customStyles.rowIcon, searchMode === 'general' && customStyles.rowIconActive]}
          />,
          `${lang.form_answer.search_action} ${trimmedQuery}`,
          lang.form_answer.search_general_hint,
          pick(false),
        )}
        {renderRow(
          searchMode === 'exact',
          <FormatQuoteRoundedIcon
            sx={[customStyles.rowIcon, searchMode === 'exact' && customStyles.rowIconActive]}
          />,
          `${lang.form_answer.search_action} "${trimmedQuery}"`,
          lang.form_answer.search_exact_hint,
          pick(true),
        )}
      </Box>
    );
  }

  if (searchMode === 'exact' && trimmedQuery.length > 0) {
    return (
      <Box sx={customStyles.pill}>
        <FormatQuoteRoundedIcon sx={customStyles.pillIcon} />
        <Typography sx={customStyles.pillText}>{lang.form_answer.exact_match}</Typography>
      </Box>
    );
  }

  return null;
};
